import { ReferencePreview } from "../references/ReferencePreview";
import {
  ArrowUp,
  ListPlus,
  Pencil,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { InlineComposer } from "./InlineComposer";
import {
  inlineBody,
  editInline,
  makeToken,
  type InlineReference,
} from "./inline-references";
import { referenceLabels } from "../references/reference-catalog";
import { MessageFiles, MessageUpload } from "./MessageFiles";
import type { MessageFile, MessageSearch } from "../../api/messaging.api";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { errorText, useCommand } from "./command";
import {
  messagingApi,
  type Conversation,
  type Directory,
  type Message,
  openMessaging,
} from "../../api/messaging.api";
import { ReferencePicker } from "../references/ReferencePicker";
import { type WorkspaceReference } from "../../api/references.api";
import { useAuthStore } from "../../store/auth.store";
import { http } from "../../api/http";
import { PresenceDot, PresenceLabel, PresenceText } from "./presence";
import { StaffPhoto } from "../photos/ProfilePhotos";
import { ConfirmDialog } from "../charte";
import { TEAM_KIND_LABEL } from "./Inbox";
import {
  BackButton,
  CONTINUED_MS,
  ConversationAvatar,
  ConversationSheet,
  IconButton,
  clock,
  useAutoGrow,
} from "./parts";
import { initials } from "./student-shared";

export function TaskMessageSource({ taskId }: { taskId: string }) {
  const user = useAuthStore((s) => s.user?.id),
    school = useAuthStore((s) => s.activeSchoolId);
  const source = useQuery({
    queryKey: ["message-task-source", user, school, taskId],
    queryFn: () => messagingApi.source(taskId),
    retry: false,
    gcTime: 0,
  });
  if (source.isError) return null;
  return (
    <>
      {source.data?.items?.map((s) => (
        <button
          key={s.messageId}
          className="btn btn--ghost btn--sm"
          onClick={() => openMessaging(s.conversationId, s.sequence)}
        >
          Ouvrir le message source
        </button>
      ))}
    </>
  );
}
function Participants({
  conversation,
  directory,
  onClose,
}: {
  conversation: Conversation;
  directory: Directory;
  onClose: () => void;
}) {
  const user = useAuthStore((s) => s.user?.id),
    command = useCommand();
  const q = useQuery({
    queryKey: ["message-members", conversation.id, user],
    queryFn: () => messagingApi.members(conversation.id),
    retry: false,
    gcTime: 0,
  });
  const [selected, setSelected] = useState<string[] | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const ids =
    selected ?? q.data?.filter((p) => !p.leftAt).map((p) => p.id) ?? [];
  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await messagingApi.setMembers(conversation.id, ids, command(ids));
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="messaging-form">
      <p className="messaging-hint">
        Un ajout donne accès à tout l’historique. Un retrait coupe l’accès.
      </p>
      {q.isError ? (
        <p role="alert">Liste indisponible.</p>
      ) : (
        <div className="messaging-members">
          {directory.items
            .filter(
              (u) =>
                conversation.type !== "SCHOOL" ||
                u.schoolId === conversation.schoolId ||
                !u.schoolId,
            )
            .map((u) => (
              <label key={u.id}>
                <input
                  type="checkbox"
                  disabled={
                    !conversation.canManage ||
                    busy ||
                    u.id === user ||
                    q.isPending
                  }
                  checked={ids.includes(u.id)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked
                        ? [...ids, u.id]
                        : ids.filter((id) => id !== u.id),
                    )
                  }
                />
                <PresenceDot presence={u.presence} />
                {u.firstName} {u.lastName}
                <PresenceText presence={u.presence} />
              </label>
            ))}
        </div>
      )}
      {conversation.canManage && (
        <div className="messaging-form-actions">
          <button
            disabled={busy || q.isPending || q.isError}
            className="btn btn--primary"
            onClick={() => void save()}
          >
            Enregistrer les participants
          </button>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
function MessageTask({
  conversation,
  message,
  directory,
  onClose,
  onDone,
}: {
  conversation: string;
  message: Message;
  directory: Directory;
  onClose: () => void;
  onDone: () => void;
}) {
  const [school, setSchool] = useState(directory.schools[0]?.id ?? ""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const command = useCommand(),
    lock = useRef(false);
  const members = useQuery({
    queryKey: ["messaging-task-members", school],
    queryFn: async () =>
      (
        await http.get<{ id: string; firstName: string; lastName: string }[]>(
          "/v1/workspace/tasks/members",
          { headers: { "X-School-Id": school } },
        )
      ).data,
    enabled: !!school,
    retry: false,
  });
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (lock.current) return;
    const d = new FormData(e.currentTarget),
      value = (k: string) => String(d.get(k) ?? "");
    const ref = message.references.find(
      (r) =>
        r.available &&
        r.schoolId === school &&
        r.entityId === value("reference"),
    );
    const payload = {
      schoolId: school,
      title: value("title"),
      description: value("description"),
      priority: "normal",
      ...(value("dueDate") ? { dueDate: value("dueDate") } : {}),
      ...(value("assignee") ? { assigneeId: value("assignee") } : {}),
      ...(ref
        ? { reference: { entityType: ref.entityType, entityId: ref.entityId } }
        : {}),
    };
    lock.current = true;
    setBusy(true);
    try {
      await messagingApi.task(conversation, message.id, {
        ...payload,
        requestId: command(payload),
      });
      onDone();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="messaging-form">
      <p className="messaging-hint">
        Le titre, la description et l’objet choisi seront visibles à l’équipe de
        cette école. Le message privé reste réservé à ses participants.
      </p>
      <fieldset disabled={busy}>
        <label>
          École
          <select value={school} onChange={(e) => setSchool(e.target.value)}>
            {directory.schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Titre
          <input name="title" required maxLength={180} />
        </label>
        <label>
          Description
          <textarea name="description" maxLength={5000} />
        </label>
        <label>
          Échéance
          <input name="dueDate" type="date" />
        </label>
        <label>
          Responsable
          <select name="assignee">
            <option value="">Non attribuée</option>
            {members.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Contexte métier
          <select name="reference">
            <option value="">Sans référence</option>
            {message.references
              .filter((r) => r.available && r.schoolId === school)
              .map(
                (r) =>
                  r.available && (
                    <option key={r.entityId} value={r.entityId}>
                      {r.label}
                    </option>
                  ),
              )}
          </select>
        </label>
        <div className="messaging-form-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Annuler
          </button>
          <button className="btn btn--primary">Créer la tâche</button>
        </div>
      </fieldset>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}

const REACTIONS = ["👍", "✅", "👀"];
const dayKey = (iso: string) => new Date(iso).toDateString();

export function ConversationView({
  conversation,
  directory,
  shared,
  clearShared,
  initialBefore,
  onBack,
}: {
  conversation: Conversation;
  directory: Directory;
  shared: WorkspaceReference | null;
  clearShared: () => void;
  initialBefore?: number;
  onBack: () => void;
}) {
  const user = useAuthStore((s) => s.user),
    school = useAuthStore((s) => s.activeSchoolId),
    switchSchool = useAuthStore((s) => s.setActiveSchool),
    navigate = useNavigate(),
    qc = useQueryClient();
  const [files, setFiles] = useState<MessageFile[]>([]),
    [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState<MessageSearch>({}),
    [searchRef, setSearchRef] = useState<WorkspaceReference | null>(null);
  const [before, setBefore] = useState<number | undefined>(initialBefore),
    [text, setText] = useState(""),
    [refs, setRefs] = useState<InlineReference[]>([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [editing, setEditing] = useState<Message | null>(null),
    [task, setTask] = useState<Message | null>(null),
    // Panneau ouvert par-dessus le fil : participants ou recherche (la tâche a le sien).
    [sheet, setSheet] = useState<"participants" | "search" | null>(null),
    [deleting, setDeleting] = useState<Message | null>(null);
  const lock = useRef(false),
    command = useCommand(),
    composer = useRef<HTMLFormElement>(null);
  useAutoGrow(composer, text);
  const q = useQuery({
    queryKey: [
      "messages",
      user?.id,
      conversation.id,
      before,
      search,
      user?.role,
      school,
    ],
    queryFn: () => messagingApi.messages(conversation.id, before, search),
    refetchInterval: (q) => (q.state.error ? false : 5000),
    refetchIntervalInBackground: false,
    retry: false,
    gcTime: 0,
  });
  const timeline = useRef<HTMLOListElement>(null);
  const followLatest = useRef(true);
  const latest = q.data?.items[q.data.items.length - 1];
  const latestId = latest?.id;
  useEffect(() => {
    if (!before && followLatest.current && timeline.current)
      timeline.current.scrollTop = timeline.current.scrollHeight;
  }, [latestId, before]);
  const searching = Object.values(search).some(Boolean);

  // Lecture : la conversation ouverte est lue jusqu'au dernier message affiché, comme les fils
  // avec élèves. Pas pendant une recherche ni en remontant l'historique, ni onglet masqué.
  const lastSequence = !before && !searching ? latest?.sequence : undefined;
  const marked = useRef(0);
  useEffect(() => {
    if (!lastSequence) return;
    const mark = () => {
      if (
        lastSequence <= marked.current ||
        document.visibilityState !== "visible"
      )
        return;
      const previous = marked.current;
      marked.current = lastSequence;
      messagingApi.read(conversation.id, lastSequence).then(
        () => void qc.invalidateQueries({ queryKey: ["messaging-list"] }),
        // Sans gravité : le « non lu » reste affiché, la lecture sera retentée au prochain message.
        () => {
          if (marked.current === lastSequence) marked.current = previous;
        },
      );
    };
    mark();
    document.addEventListener("visibilitychange", mark);
    return () => document.removeEventListener("visibilitychange", mark);
  }, [conversation.id, lastSequence, qc]);

  const consumedShare = useRef<WorkspaceReference | null>(null);
  useEffect(() => {
    if (!shared) {
      consumedShare.current = null;
      return;
    }
    if (shared !== consumedShare.current) {
      consumedShare.current = shared;
      const token = makeToken(referenceLabels[shared.entityType], refs);
      setRefs((old) => [...old, { ...shared, token }]);
      setText((old) => old + (old ? " " : "") + token + " ");
      clearShared();
    }
    // clearShared consumes this one-shot external share request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared, clearShared]);
  const refresh = () => {
    void qc.invalidateQueries({
      queryKey: ["messages", user?.id, conversation.id],
    });
    void qc.invalidateQueries({ queryKey: ["messaging-list"] });
  };
  async function perform(
    payload: unknown,
    fn: (request: string) => Promise<unknown>,
  ) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await fn(command(payload));
      command.reset();
      refresh();
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (
      busy ||
      uploading ||
      (!text.trim() && !files.length && !editing?.files?.length)
    )
      return;
    const body = inlineBody(text, refs);
    if (
      body.segments.length > 100 ||
      body.segments.filter((s) => s.kind === "reference").length > 50
    ) {
      setError(
        "Ce message contient trop de liens. Répartissez-les sur plusieurs messages.",
      );
      return;
    }
    const payload = [
      editing?.id,
      editing?.version,
      body,
      files.map((f) => f.id),
    ];
    if (
      await perform(payload, (id) =>
        editing
          ? messagingApi.edit(conversation.id, editing, id, body)
          : messagingApi.send(
              conversation.id,
              id,
              body,
              files.map((f) => f.id),
            ),
      )
    ) {
      setText("");
      setFiles([]);
      setRefs([]);
      setEditing(null);
      setBefore(undefined);
    }
  }
  async function openObject(href: string, sourceSchool?: string) {
    if (sourceSchool && sourceSchool !== school)
      await switchSchool(sourceSchool);
    navigate(href);
  }
  function react(m: Message, emoji: string) {
    const on = !m.reactions.some((r) => r.emoji === emoji && r.mine);
    void perform([m.id, emoji, on], (id) =>
      messagingApi.react(conversation.id, m.id, emoji, on, id),
    );
  }
  if (q.isError)
    return (
      <section className="messaging-state">
        <BackButton onBack={onBack} />
        <div className="messaging-state-box">
          <p role="alert">
            Conversation indisponible ou connexion interrompue. Aucun contenu
            conservé n’est affiché.
          </p>
          <div className="messaging-state-actions">
            <button
              className="btn btn--secondary"
              onClick={() => void q.refetch()}
            >
              Réessayer
            </button>
          </div>
        </div>
      </section>
    );
  const writable = directory.role !== "read_only";
  const canTask = ["owner", "manager", "agent"].includes(directory.role);
  const items = q.data?.items ?? [];
  return (
    <section className="messaging-conversation">
      <header className="messaging-conv-head">
        <BackButton onBack={onBack} />
        <ConversationAvatar
          kind="team"
          name={conversation.title}
          staffId={conversation.type === "DIRECT" ? conversation.peerId : null}
          presence={conversation.presence}
        />
        <div className="messaging-conv-title">
          <h2>{conversation.title}</h2>
          <p className="messaging-conv-sub">
            <span>{TEAM_KIND_LABEL[conversation.type]}</span>
            <PresenceLabel presence={conversation.presence} />
          </p>
        </div>
        <div className="messaging-conv-actions">
          <IconButton
            icon={Search}
            label="Rechercher"
            pressed={sheet === "search"}
            onClick={() => setSheet(sheet === "search" ? null : "search")}
          />
          <IconButton
            icon={Users}
            label="Participants"
            pressed={sheet === "participants"}
            onClick={() =>
              setSheet(sheet === "participants" ? null : "participants")
            }
          />
        </div>
      </header>
      {searching && (
        <div className="messaging-banner" role="status">
          <span>
            Résultats de recherche · texte courant, hors messages supprimés
          </span>
          <button
            type="button"
            onClick={() => {
              setSearch({});
              setSearchRef(null);
              setBefore(undefined);
            }}
          >
            Effacer la recherche
          </button>
        </div>
      )}
      <ol
        className="messaging-messages messaging-timeline"
        ref={timeline}
        onScroll={(e) => {
          const el = e.currentTarget;
          followLatest.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {(q.data?.hasMore || before) && (
          <li className="messaging-history-nav">
            {q.data?.hasMore && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setBefore(q.data!.items[0].sequence)}
              >
                Messages précédents
              </button>
            )}
            {before && (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setBefore(undefined)}
              >
                Derniers messages
              </button>
            )}
          </li>
        )}
        {q.isPending && (
          <li className="messaging-timeline-status">
            <p role="status">Chargement…</p>
          </li>
        )}
        {!q.isPending && !items.length && (
          <li className="messaging-timeline-status">
            <p>
              {searching
                ? "Aucun message ne correspond à cette recherche."
                : "Aucun message pour le moment. Écrivez le premier."}
            </p>
          </li>
        )}
        {items.map((m, index) => {
          const previous = items[index - 1];
          const newDay =
            !previous || dayKey(previous.createdAt) !== dayKey(m.createdAt);
          // Suite d'un message de la même personne : ni avatar ni nom, l'heure au survol.
          const continued =
            !newDay &&
            previous.authorId === m.authorId &&
            !previous.deletedAt &&
            Date.parse(m.createdAt) - Date.parse(previous.createdAt) <
              CONTINUED_MS;
          const mine = m.authorId === user?.id;
          const used = m.reactions.filter((r) => r.count > 0);
          const free = REACTIONS.filter(
            (emoji) => !used.some((r) => r.emoji === emoji),
          );
          return (
            <li
              key={m.id}
              className={
                "messaging-message" +
                (mine ? " messaging-message--mine" : "") +
                (continued ? " messaging-message--continued" : "")
              }
            >
              {newDay && (
                <div className="messaging-day">
                  <time dateTime={m.createdAt}>
                    {new Date(m.createdAt).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </time>
                </div>
              )}
              <div className="messaging-message-main">
                {continued ? (
                  <time
                    className="messaging-continued-time"
                    dateTime={m.createdAt}
                    title={new Date(m.createdAt).toLocaleString("fr-FR")}
                  >
                    {clock(m.createdAt)}
                  </time>
                ) : (
                  <div className="messaging-author-line">
                    <span
                      className="messaging-message-avatar"
                      aria-hidden="true"
                    >
                      {initials(m.authorName)}
                      <StaffPhoto
                        userId={m.authorId}
                        className="messaging-avatar-photo"
                      />
                    </span>
                    <strong>{m.authorName}</strong>
                    <time
                      dateTime={m.createdAt}
                      title={new Date(m.createdAt).toLocaleString("fr-FR")}
                    >
                      {clock(m.createdAt)}
                    </time>
                  </div>
                )}
                {m.deletedAt ? (
                  <p className="messaging-deleted">Message supprimé</p>
                ) : (
                  <>
                    <p className="messaging-text">
                      {m.content?.segments.map((segment, index) => {
                        if (segment.kind === "text")
                          return <span key={index}>{segment.text}</span>;
                        const r = m.references.find(
                          (r) =>
                            r.entityType === segment.reference.entityType &&
                            r.entityId === segment.reference.entityId,
                        );
                        return r?.available ? (
                          <button
                            key={index}
                            type="button"
                            className="messaging-inline-reference"
                            title={referenceLabels[r.entityType]}
                            onClick={() =>
                              void openObject(r.href, r.schoolId).catch(() =>
                                setError("Ouverture indisponible."),
                              )
                            }
                          >
                            #{r.label}
                          </button>
                        ) : (
                          <span
                            key={index}
                            className="messaging-inline-unavailable"
                            title="Vous ne pouvez pas consulter cet objet. Il peut avoir été supprimé ou ses droits modifiés."
                          >
                            Objet inaccessible
                          </span>
                        );
                      })}
                      {m.editedAt && (
                        <small className="messaging-edited"> (modifié)</small>
                      )}
                    </p>
                    {m.references.map((r) => (
                      <ReferencePreview
                        key={r.entityType + r.entityId}
                        item={r}
                      />
                    ))}
                    {!!m.files?.length && (
                      <MessageFiles
                        conversation={conversation.id}
                        files={m.files}
                        onError={setError}
                      />
                    )}
                    {!!used.length && (
                      <div className="messaging-reactions">
                        {used.map((r) => (
                          <button
                            key={r.emoji}
                            type="button"
                            aria-label={`Réagir ${r.emoji}`}
                            aria-pressed={r.mine}
                            disabled={busy || !writable}
                            onClick={() => react(m, r.emoji)}
                          >
                            {r.emoji} {r.count}
                          </button>
                        ))}
                      </div>
                    )}
                    {!!m.tasks.length && (
                      <div className="messaging-task-result">
                        <ListPlus size={14} aria-hidden="true" />
                        <span>Tâche créée à partir de ce message</span>
                        {m.tasks.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() =>
                              void openObject(
                                "/workspace/tasks?taskId=" + t.id,
                                t.schoolId,
                              )
                            }
                          >
                            Ouvrir la tâche liée
                          </button>
                        ))}
                      </div>
                    )}
                    {(writable || canTask) && (
                      <div
                        className="messaging-hoverbar"
                        role="group"
                        aria-label="Actions du message"
                      >
                        {writable &&
                          free.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              aria-label={`Réagir ${emoji}`}
                              aria-pressed={false}
                              title={`Réagir ${emoji}`}
                              disabled={busy}
                              onClick={() => react(m, emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        {writable &&
                          mine &&
                          Date.now() - Date.parse(m.createdAt) < 900000 && (
                            <button
                              type="button"
                              aria-label="Modifier"
                              title="Modifier (15 minutes après l’envoi)"
                              onClick={() => {
                                setFiles([]);
                                setEditing(m);
                                const draft = editInline(m);
                                setText(draft.text);
                                setRefs(draft.refs);
                              }}
                            >
                              <Pencil size={15} aria-hidden="true" />
                            </button>
                          )}
                        {writable && mine && (
                          <button
                            type="button"
                            aria-label="Supprimer"
                            title="Supprimer"
                            onClick={() => setDeleting(m)}
                          >
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        )}
                        {canTask && (
                          <button
                            type="button"
                            aria-label="Créer une tâche"
                            title="Créer une tâche à partir de ce message"
                            onClick={() => setTask(m)}
                          >
                            <ListPlus size={15} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {error && (
        <p role="alert" className="messaging-error">
          {error}
        </p>
      )}
      {writable && (
        <form ref={composer} className="messaging-composer" onSubmit={submit}>
          {editing && (
            <div className="messaging-editing">
              <Pencil size={14} aria-hidden="true" />
              <span>Modification d’un message</span>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setText("");
                  setRefs([]);
                }}
              >
                Annuler la modification
              </button>
            </div>
          )}
          <InlineComposer
            text={text}
            refs={refs}
            onChange={(value, references) => {
              setText(value);
              setRefs(references);
            }}
            scope={[school ?? "", user?.id ?? ""]}
            disabled={busy}
            placeholder={`Écrire à ${conversation.title}…`}
            sendAction={
              editing ? (
                <button
                  type="submit"
                  className="btn btn--primary btn--sm"
                  disabled={
                    busy ||
                    uploading ||
                    (!text.trim() && !refs.length && !editing.files?.length)
                  }
                >
                  Enregistrer le message
                </button>
              ) : (
                <button
                  type="submit"
                  className="messaging-send"
                  aria-label="Envoyer"
                  title="Envoyer (Entrée)"
                  disabled={
                    busy ||
                    uploading ||
                    (!text.trim() && !refs.length && !files.length)
                  }
                >
                  <ArrowUp size={18} strokeWidth={2.2} aria-hidden="true" />
                </button>
              )
            }
            attachment={
              !editing && (
                <MessageUpload
                  conversation={conversation.id}
                  files={files}
                  onFiles={setFiles}
                  onBusy={setUploading}
                  onError={setError}
                  disabled={busy}
                />
              )
            }
          />
        </form>
      )}
      {sheet === "participants" && (
        <ConversationSheet title="Participants" onClose={() => setSheet(null)}>
          <Participants
            conversation={conversation}
            directory={directory}
            onClose={() => setSheet(null)}
          />
        </ConversationSheet>
      )}
      {sheet === "search" && (
        <ConversationSheet
          title="Rechercher dans la conversation"
          onClose={() => setSheet(null)}
        >
          <form
            className="messaging-form"
            onSubmit={(e) => {
              e.preventDefault();
              const data = new FormData(e.currentTarget);
              setSearch({
                q: String(data.get("q") || "").trim() || undefined,
                authorId: String(data.get("author") || "") || undefined,
                ...(searchRef
                  ? {
                      entityType: searchRef.entityType,
                      entityId: searchRef.entityId,
                    }
                  : {}),
              });
              setBefore(undefined);
              setSheet(null);
            }}
          >
            <label>
              Texte du message
              <input
                name="q"
                maxLength={120}
                minLength={2}
                defaultValue={search.q ?? ""}
              />
            </label>
            <label>
              Auteur
              <select
                name="author"
                aria-label="Auteur"
                defaultValue={search.authorId ?? ""}
              >
                <option value="">Tous les auteurs</option>
                {directory.items.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </label>
            <details className="messaging-details">
              <summary>Filtrer par objet métier</summary>
              <ReferencePicker
                scope={[school ?? "", user?.id ?? ""]}
                disabled={busy}
                onAdd={setSearchRef}
              />
              {searchRef && (
                <button
                  type="button"
                  className="messaging-chip"
                  onClick={() => setSearchRef(null)}
                >
                  Retirer le filtre {searchRef.entityType}
                  <X size={13} aria-hidden="true" />
                </button>
              )}
            </details>
            <div className="messaging-form-actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setSheet(null)}
              >
                Annuler
              </button>
              <button className="btn btn--primary">
                Rechercher l’historique
              </button>
            </div>
          </form>
        </ConversationSheet>
      )}
      {task && (
        <ConversationSheet
          title="Créer une tâche"
          onClose={() => setTask(null)}
        >
          <MessageTask
            conversation={conversation.id}
            message={task}
            directory={directory}
            onClose={() => setTask(null)}
            onDone={refresh}
          />
        </ConversationSheet>
      )}
      <ConfirmDialog
        open={!!deleting}
        tone="danger"
        title="Supprimer ce message ?"
        confirmLabel="Supprimer le message"
        cancelLabel="Annuler"
        pending={busy}
        error={deleting && error ? error : undefined}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          void perform(["delete", deleting.id, deleting.version], (id) =>
            messagingApi.remove(conversation.id, deleting, id),
          ).then((ok) => {
            if (ok) setDeleting(null);
          });
        }}
      >
        Le message est masqué pour tous les participants. Une trace historique
        est conservée.
      </ConfirmDialog>
    </section>
  );
}
